import { useMemo } from 'react';

import { AdditionalEntitlement, MedicinePrescription } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { StandardSpinner } from '@dreryk/react-components';

import { MagistralDrugHeading } from '../../../magistralDrug/MagistralDrugHeading';
import { MagistralDrugSubheading } from '../../../magistralDrug/MagistralDrugSubheading';
import { useReimbursementSelector } from '../../../reimbursement/reimbursement.hooks';
import { ActiveIngredientsQuantitiesTextValue } from '../../prescription/activeIngredients/ActiveIngredientsTextValue';
import { AdditionalEntitlementMultiSelect } from '../../prescription/additionalEntitlements/AdditionalEntitlementMultiSelect';
import {
   useAdditionalEntitlementsSelector,
   useSelectedAdditionalEntitlementsSync,
} from '../../prescription/additionalEntitlements/additionalEntitlements.hooks';
import { useAdditionalRequirementsSelector } from '../../prescription/additionalRequirements/additionalRequirements.hooks';
import { AdditionalRequirementsMultiSelect } from '../../prescription/additionalRequirements/AdditionalRequirementsMultiSelect';
import { DosageInput } from '../../prescription/dosage/DosageInput';
import { IssuedFromDatePicker } from '../../prescription/issuedDate/IssuedFromDatePicker';
import { IssuedToTextValue } from '../../prescription/issuedDate/IssuedToTextValue';
import { QuantityInput } from '../../prescription/quantity/QuantityInput';
import { PrescriptionCard } from '../../prescription/PrescriptionCard';
import { DraftPrescriptionRemoveIcon } from '../common/DraftPrescriptionRemoveIcon';
import { DraftReimbursementSelector } from '../common/DraftReimbursementSelector';
import { RuleVerificationResultsStatus } from '../common/RuleVerificationResultsStatus';
import { DraftPrescriptionIndex, DraftPrescriptionsFormController } from '../DraftPrescriptionsForm.types';

import { DraftMagistralDrugPrescription } from './DraftMagistralDrugPrescriptionField.types';
import { createDraftMagistralDrugPrescriptionsFieldNameFactory } from './DraftMagistralDrugPrescriptionField.utils';
import { DraftMagistralDrugPrescriptionGrid } from './DraftMagistralDrugPrescriptionGrid';

type DraftMagistralDrugPrescriptionFieldProps = DraftMagistralDrugPrescription &
   Pick<DraftPrescriptionsFormController, 'register' | 'setValue' | 'trigger' | 'watch'> & {
      readonly index: DraftPrescriptionIndex;
      readonly onRemove: VoidFunction;
      readonly medicinePrescription: MedicinePrescription;
      readonly isFetching: boolean;
      readonly visitDiagnoses: VisitDiagnosis[];
   };

// eslint-disable-next-line max-lines-per-function
export const DraftMagistralDrugPrescriptionField = (props: DraftMagistralDrugPrescriptionFieldProps): JSX.Element => {
   const createFieldName = createDraftMagistralDrugPrescriptionsFieldNameFactory(props.index);
   const magistralDrug = props.medicinePrescription.magistralDrug ?? props.selectedByUser.magistralDrug!;

   const computedAdditionalEntitlements = useAdditionalEntitlementsSelector({
      initialAdditionalEntitlementTypes: props.initialData.additionalEntitlementTypes,
      selectedPatientReimbursementByUser: undefined,
      medicinePrescription: props.medicinePrescription,
   });
   const computedReimbursement = useReimbursementSelector({
      initialReimbursementType: props.initialData.reimbursementType,
      selectedPatientReimbursementByUser: undefined,
      additionalEntitlements: computedAdditionalEntitlements,
      reimbursements: props.medicinePrescription.reimbursements,
   });
   const computedAdditionalRequirements = useAdditionalRequirementsSelector({
      initialAdditionalRequirementTypes: props.initialData.additionalRequirementsTypes,
   });

   const selectedEntitlementsFieldName = createFieldName('additionalEntitlements');
   const currentSelectedEntitlements = props.watch<
      typeof selectedEntitlementsFieldName,
      AdditionalEntitlement[] | undefined
   >(selectedEntitlementsFieldName);
   const selectedEntitlements = useMemo(() => currentSelectedEntitlements ?? computedAdditionalEntitlements, [
      computedAdditionalEntitlements,
      currentSelectedEntitlements,
   ]);

   useSelectedAdditionalEntitlementsSync(
      props.medicinePrescription,
      selectedEntitlements,
      props.setValue,
      createFieldName('additionalEntitlements')
   );

   return (
      <PrescriptionCard
         testId={props.index}
         heading={
            <MagistralDrugHeading
               name={magistralDrug.name}
               magistralDrugMedicineRawMaterials={magistralDrug.magistralDrugMedicineRawMaterials}
            />
         }
         subheading={
            <MagistralDrugSubheading
               name={magistralDrug.name}
               magistralDrugMedicineRawMaterials={magistralDrug.magistralDrugMedicineRawMaterials}
               additionalDetails={magistralDrug.additionalDetails}
               form={magistralDrug.form}
            />
         }
         options={
            <>
               {props.isFetching ? <StandardSpinner /> : null}
               <DraftPrescriptionRemoveIcon onRemove={props.onRemove} />
            </>
         }
      >
         <RuleVerificationResultsStatus ruleVerificationResults={props.initialData.ruleValidationResults} />

         <DraftReimbursementSelector
            register={props.register}
            name={createFieldName('reimbursement')}
            reimbursements={props.medicinePrescription.reimbursements}
            defaultValue={computedReimbursement}
            visitDiagnoses={props.visitDiagnoses}
            selectedEntitlements={selectedEntitlements}
         />

         <DraftMagistralDrugPrescriptionGrid
            dosage={
               <DosageInput
                  register={props.register}
                  trigger={props.trigger}
                  name={createFieldName('dosage')}
                  defaultValue={props.data.dosage}
                  required
               />
            }
            issuedFrom={
               <IssuedFromDatePicker
                  register={props.register}
                  name={createFieldName('issuedFrom')}
                  defaultValue={props.data.issuedFrom}
               />
            }
            quantity={
               <QuantityInput
                  register={props.register}
                  trigger={props.trigger}
                  name={createFieldName('quantity')}
                  defaultValue={props.initialData.quantity}
                  dosageExceedingPackages={false}
               />
            }
            additionalRequirements={
               <AdditionalRequirementsMultiSelect
                  register={props.register}
                  name={createFieldName('additionalRequirements')}
                  defaultValue={computedAdditionalRequirements}
               />
            }
            additionalEntitlements={
               <AdditionalEntitlementMultiSelect
                  register={props.register}
                  name={createFieldName('additionalEntitlements')}
                  defaultValue={computedAdditionalEntitlements}
                  additionalEntitlements={props.medicinePrescription.additionalEntitlements}
               />
            }
            issuedTo={
               <IssuedToTextValue
                  name={createFieldName('issuedTo')}
                  setValue={props.setValue}
                  issuedFrom={props.watch(createFieldName('issuedFrom'))}
                  additionalRequirements={props.watch(createFieldName('additionalRequirements'))}
                  defaultValue={props.data.issuedTo}
               />
            }
            activeIngredients={
               <ActiveIngredientsQuantitiesTextValue
                  medicinePrescription={props.medicinePrescription}
                  quantity={props.watch(createFieldName('quantity'))}
               />
            }
         />
      </PrescriptionCard>
   );
};
